import React, {
  FunctionComponent,
  useCallback,
  useRef,
  type MouseEvent,
} from 'react';
import { useDispatch } from 'react-redux';
import { twJoin } from 'tailwind-merge';

import { useDynamicSaga } from '@/core/applicationState/hooks/useDynamicSaga';
import { IconButton } from '@/core/tamagoshiTailwind/components/IconButton/IconButton';
import { NavigationButton } from '@/core/tamagoshiTailwind/components/NavigationButton/NavigationButton';
import { SectionButton } from '@/core/tamagoshiTailwind/components/SectionButton/SectionButton';
import { ArrowLeftIcon } from '@/core/tamagoshiTailwind/icons/ArrowLeftIcon';
import { twMerge } from '@/core/tamagoshiTailwind/util/twMerge';
import { Gtm } from '@/core/tracking/utils/Gtm';
import {
  setMenuVisibility,
  setSelectedCategory,
  setSelectedFamily,
} from '@/productDiscovery/HeaderLeonidas/HeaderLeonidas.actions';
import { useSelectedCategory } from '@/productDiscovery/HeaderLeonidas/hooks/useMenuVisibility';
import type { MenuCategory } from '@/productDiscovery/HeaderLeonidas/interfaces/MenuTree.model';
import { rootSaga } from '@/productDiscovery/HeaderLeonidas/sagas/BannerCampaignsMenuCategory.client.saga';
import {
  GTM_CLICK_ON_MENU_ITEM,
  GTM_CLICK_ON_SEE_ALL_CATEGORIES,
} from '@/productDiscovery/HeaderLeonidas/services/tracking/gtmEvents';
import { debounce } from '@/productDiscovery/HeaderLeonidas/utils/debounce';
import { isMobile } from '@/productDiscovery/HeaderLeonidas/utils/isMobile';

interface MenuCategoriesProps {
  selectedFamily: MenuCategory;
}

export const MenuCategories: FunctionComponent<MenuCategoriesProps> = ({
  selectedFamily,
}: MenuCategoriesProps) => {
  useDynamicSaga('menuCampaignsSaga', rootSaga);
  const dispatch = useDispatch();
  const selectedCategory = useSelectedCategory();
  const overTimeout = useRef<any>();

  const handleClickSeeAllCategories = () => {
    dispatch(setMenuVisibility(false));
    Gtm.push(GTM_CLICK_ON_SEE_ALL_CATEGORIES(selectedFamily));
  };

  const handleClickPrevious = (e: MouseEvent<HTMLButtonElement>) => {
    dispatch(setSelectedFamily(null));
    dispatch(setSelectedCategory(null));
    e.stopPropagation();
  };

  const handleOver = useCallback(() => {
    dispatch(setSelectedCategory(null));
  }, [dispatch]);

  const select = debounce((category: MenuCategory) => {
    dispatch(setSelectedCategory(category));
  }, 20);

  const onClick = (category: MenuCategory) => {
    if (isMobile()) {
      const menuItem = {
        categoryName: selectedFamily.name,
        subCategoryName: category.name,
        subSubCategoryName: '',
        url: category.url,
        level: 1,
      };

      dispatch(setSelectedCategory(category));
      Gtm.push(GTM_CLICK_ON_MENU_ITEM(menuItem));
    }
  };

  const mouseOver = useCallback(
    (category: MenuCategory) => {
      if (isMobile()) {
        return;
      }

      overTimeout.current = setTimeout(() => select(category), 200);
    },
    [select],
  );

  const mouseOut = useCallback(() => {
    if (isMobile()) {
      return;
    }

    if (overTimeout.current) {
      clearTimeout(overTimeout.current);
    }
  }, []);

  return (
    <div
      className={twJoin(
        `absolute bottom-0 left-0 top-0 z-[12] w-full bg-foundation-inverted
        desktop:w-[360px] desktop:border-r desktop:border-foundation-primary`,
        selectedCategory && 'hidden desktop:block',
      )}
    >
      <div
        className={twMerge(
          `flex h-[64px] w-full items-center gap-s border-b
          border-foundation-primary pl-m pr-xxxl`,
          selectedCategory && 'pr-m',
        )}
        onMouseEnter={handleOver}
      >
        <IconButton
          aria-label={`previous-${selectedFamily.name}`}
          data-testid={`previous-${selectedFamily.name}`}
          icon={ArrowLeftIcon}
          onClick={handleClickPrevious}
          size="M"
        />
        <NavigationButton
          data-testid={`see-all-${selectedFamily.name}`}
          href={selectedFamily.url}
          onClick={handleClickSeeAllCategories}
          size="M"
          text={selectedFamily.name}
          variant="link"
        />
      </div>
      <ul className="h-[calc(100%-64px)] overflow-y-auto">
        {selectedFamily.children?.map((category) => (
          <li
            key={category.id}
            onFocus={() => select(category)}
            onMouseEnter={() => mouseOver(category)}
            onMouseLeave={mouseOut}
            data-testid="menu-category-item"
          >
            <SectionButton
              title={category.name}
              onClick={() => onClick(category)}
              active={category.id === selectedCategory?.id}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};
